import React, { useState, useEffect } from 'react';
import { db } from "../firebase-config";
import { collection, getDocs, getDoc, doc } from "firebase/firestore";
import { useNavigate, Link } from 'react-router-dom';
import AnimatedHero from "../Components/AnimatedHero";
import PageTransition from "../Components/PageTransition";

// Loader with theme colors
const ModernLoader = () => (
  <div className="text-center py-5">
    <div className="spinner-border text-primary" role="status"></div>
    <p className="mt-3 text-secondary">Loading Services...</p>
  </div>
);

// Animated number counter for stats
const StatsCounter = ({ number, label }) => {
  const [count, setCount] = useState(0);

  useEffect(() => setCount(0), [number]);

  useEffect(() => {
    if (typeof number !== "number" || count >= number) return;
    let diff = number - count;
    let increment, timeout;
    if (number < 50) { increment = 1; timeout = 25; }
    else if (number < 300) { increment = Math.ceil(diff / 8); timeout = 18; }
    else if (number < 2000) { increment = Math.ceil(diff / 5); timeout = 8; }
    else { increment = Math.ceil(diff / 3); timeout = 2; }
    const timer = setTimeout(() => {
      setCount(prev => Math.min(prev + increment, number));
    }, timeout);
    return () => clearTimeout(timer);
  }, [count, number]);

  return (
    <div className="text-center">
      <div className="h2 text-primary fw-bold">{count}+</div>
      <div className="text-dark">{label}</div>
    </div>
  );
};

// Static core services
const coreServices = [
  {
    icon: "bi-book",
    title: "Free Tuition & Learning Space",
    description: "A free tuition center for underprivileged students, with study areas, a small library, yoga sessions, and support for students with learning disabilities.",
    color: "var(--secondary)"
  },
  {
    icon: "bi-tree",
    title: "Eco Clubs & School Partnerships",
    description: "Partnering with local schools to promote environmental responsibility through Eco Clubs, activities, and awareness campaigns.",
    color: "var(--secondary)"
  },
  {
    icon: "bi-flower2",
    title: "Farming & Sustainable Agriculture",
    description: "Training and awareness programs on organic farming, seed preservation, and sustainable agricultural practices.",
    color: "var(--secondary)"
  },
  {
    icon: "bi-droplet-half",
    title: "Pond Restoration & Water Conservation",
    description: "Cleaning and restoration of local ponds to support biodiversity and conserve water resources.",
    color: "var(--secondary)"
  },
  {
    icon: "bi-tree-fill",
    title: "Afforestation & Green Spaces",
    description: "Tree planting initiatives in schools, communities, and private lands, and creation of mini forests.",
    color: "var(--secondary)"
  },
  {
    icon: "bi-journal-richtext",
    title: "Cultural Documentation",
    description: "Preserving cultural heritage by documenting and archiving the stories and performances of local folk artists.",
    color: "var(--secondary)"
  }
];

const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleCards, setVisibleCards] = useState([]);
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // Fetch stats
  useEffect(() => {
    let isMounted = true;
    async function fetchStats() {
      setStatsLoading(true);
      try {
        const snap = await getDoc(doc(db, "stats", "about"));
        if (isMounted) setStats(snap.exists() ? snap.data() : {});
      } catch (err) {
        console.error("Error fetching stats:", err);
        if (isMounted) setStats({});
      } finally {
        if (isMounted) setStatsLoading(false);
      }
    }
    fetchStats();
    return () => { isMounted = false; };
  }, []);

  // Fetch additional services
  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        const snapshot = await getDocs(collection(db, "services"));
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setServices(data);
      } catch (error) {
        console.error("Error fetching services:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  // Card fade-in animation
  useEffect(() => {
    if (loading) return;
    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const index = parseInt(entry.target.dataset.index, 10);
          if (entry.isIntersecting && !visibleCards.includes(index)) {
            setVisibleCards((prev) => [...prev, index]);
          }
        });
      },
      { threshold: 0.1 }
    );
    const cards = document.querySelectorAll('.card.service-card');
    cards.forEach((card) => observer.observe(card));
    return () => observer.disconnect();
  }, [loading, visibleCards]);

  return (
    <PageTransition className="services-page">

      {/* Hero Section */}
      <div className="hero-parallax">
        <AnimatedHero
          title="Our Services"
          subtitle="Empowering communities through education, environmental conservation, sustainable agriculture, and cultural preservation."
          className="services-hero"
          overlayColor="rgba(102,126,234,0.1)"
        />
      </div>

      {/* Stats Section */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="row g-4">
            {statsLoading ? (
              <div className="col text-center text-secondary">Loading stats...</div>
            ) : (
              <>
                <div className="col-6 col-md-3"><StatsCounter number={stats?.studentsHelped || 0} label="Students Helped" /></div>
                <div className="col-6 col-md-3"><StatsCounter number={stats?.treesPlanted || 0} label="Trees Planted" /></div>
                <div className="col-6 col-md-3"><StatsCounter number={stats?.pondsRestored || 0} label="Ponds Restored" /></div>
                <div className="col-6 col-md-3"><StatsCounter number={stats?.communitiesServed || 0} label="Communities Served" /></div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-5">
        <div className="container">

          {/* Header */}
          <div className="text-center mb-5">
            <span className="badge bg-accent text-dark mb-2">What We Do</span>
            <h2>Transforming Communities <span className=" text-accent">Through Impact</span></h2>
            <p className="secondary">
              We create lasting change through education, conservation, and cultural preservation.
            </p>
          </div>

          {/* Core Services */}
          {loading ? (
            <ModernLoader />
          ) : (
            <div className="row g-4">
              {coreServices.map((service, index) => (
                <div className="col-md-6 col-lg-4" key={service.title} data-index={index}>
                  <div className={`card service-card fade-in ${visibleCards.includes(index) ? 'show' : ''}`} style={{ '--delay': `${index * 0.2}s` }}>
                    <div className="text-center mb-3" style={{ color: service.color }}>
                      <i className={`${service.icon} fs-1`}></i>
                    </div>
                    <h5 className="mb-2 text-secondary">{service.title}</h5>
                    <p className="card-text">{service.description}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Additional Services from Firestore */}
          {services.length > 0 && (
            <div className="mt-5">
              <h3 className="mb-4">Community Initiatives</h3>
              <div className="row g-4">
                {services.map((srv, index) => (
                  <div key={srv.id || index} className="col-md-6 col-lg-4">
                    <Link to={`/services/${srv.id}`} style={{ textDecoration: "none", color: "inherit" }}>

                      <div className="card h-100 service-card-hover">
                        {srv.imageUrl && (
                          <img 
                            src={srv.imageUrl} 
                            alt={srv.title} 
                            className="card-img-top" 
                            style={{ maxHeight: "200px", objectFit: "cover" }} 
                          />
                        )}
                        <div className="card-body">
                          <h5 className="card-title">{srv.title}</h5>
                        </div>
                      </div>
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </section>

      {/* CTA */}
      <section className="py-5 bg-primary text-center text-light">
        <div className="container">
          <h2 style={{color:"var(--accent)"}}>Ready to Make a Difference?</h2>
          <p>Join us in our mission to create positive change</p>
          <button className="btn-accent mt-3" onClick={() => navigate('/donate')}>
            Get Involved <i className="bi bi-heart-fill"></i>
          </button>
        </div>
      </section>

    </PageTransition>
  );
};

export default Services;
